<template>
  <div v-if="expanded" class="expanded">
    <div class="divider"></div>
    <div class="expanded-row">
      <label class="label" for="promocode">
        {{ $t('promocode.label') }}
      </label>
      <div class="promocode-row">
        <input id="promocode" type="text" v-model="code" class="promocode-input" />
        <button type="button" class="promocode-button" :disabled="applied" @click="apply">
          <span class="spinner" v-if="isLoading">
            <spinner-component />
          </span>
          <span v-else>{{ $t('promocode.apply') }}</span>
        </button>
      </div>
    </div>
  </div>
  <button v-else class="expand-toggle blank-styling" type="button" @click="openExpandedView()">
    {{ $t('promocode.show') }}
  </button>
</template>

<script lang="ts">
import { computed, defineComponent, watch, ref } from 'vue';
import SpinnerComponent from '@/components/SpinnerComponent.vue';
import { useMixpanel } from '@/composables/useMixpanel';
import { TrackEvents } from '@/types/trackEvents';

export default defineComponent({
  name: 'PromoCode',
  components: {
    SpinnerComponent,
  },
  props: {
    expanded: {
      type: Boolean,
      required: false,
      default: false,
    },
    modelValue: {
      type: [String, null],
      required: false,
      default: '',
    },
    applied: {
      type: Boolean,
      required: false,
      default: false,
    },
    isLoading: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  emits: ['apply', 'update:expanded', 'update:modelValue'],
  setup(props, { emit }) {
    const { track } = useMixpanel();
    const hasTracked = ref(false);

    const openExpandedView = () => {
      emit('update:expanded', true);
    };

    const code = computed({
      get: () => props.modelValue,
      set: (val: string) => emit('update:modelValue', val),
    });

    const apply = () => emit('apply', props.modelValue);

    watch(
      () => props.expanded,
      (expanded) => {
        if (expanded && !hasTracked.value) {
          track(TrackEvents.PROMO_CODE_VIEW);
          hasTracked.value = true;
        }
      },
    );

    return {
      openExpandedView,
      apply,
      code,
    };
  },
});
</script>

<style scoped lang="scss">
.expand-toggle {
  color: $primary-color;
  margin-top: 28px;
  margin-left: auto;
  margin-right: auto;
  display: block;
  font-size: 1rem;
  font-weight: 500;
}

.expanded {
  margin-top: 8px;
}

.expanded-row {
  display: flex;
  flex-direction: column;
}

.promocode-row {
  display: flex;
}

.promocode-button {
  @extend %secondary-button;
  background: none;
  font-weight: bold;
  font-size: 1rem;
  min-width: 4rem;

  span:first-of-type {
    color: $color-primary;
  }

  &:disabled {
    background-color: transparent !important;
    color: lighten($color-primary, 20%) !important;
  }

  :deep(.lds-ring div) {
    border-color: $color-primary transparent transparent transparent !important;
  }

  &:active,
  &:hover {
    outline: none;
    box-shadow: none;
    background: none !important;
  }
}

.promocode-input {
  flex-grow: 1;
  margin-right: 2%;
  color: $color-space-blue;
}

.label {
  @extend %label;
}

.divider {
  width: 80%;
  margin: 34px auto 30px auto;
  border-top: 1px $color-wide-blue-yonder solid;
}
</style>
